import { ref, onMounted, onUnmounted } from "vue"
import { guid } from "@common/Guid"
import * as cqrs from "@cqrs/cqrs"
import { logInfo, logException } from "@common/Logger"

export function useQuery(queryFactory: () => any, correlationId = guid()) {
    const $query = ref<any>(null)
    const $model = ref<any>(null)
    const modelName = ref<any>(null)
    const modelFullName = ref<any>(null)
    const succeed = ref<boolean>(null)
    const authorized = ref<boolean>(null)
    const error = ref<any>(null)
    const completed = ref<boolean>(false)
    const running = ref<boolean>(false)
    const redirectUrl = ref<string>('/')

    function run(data: any = {}) {
        $query.value = queryFactory()
        const qry = Object.assign({}, $query.value, data)
        cqrs.query(qry, correlationId)
    }

    function onSetQueryStatus(status: any) {
        try {
            if (status.Query.$type != $query?.value?.$type || status.CorrelationId != correlationId) return

            //logInfo('Query status', status)

            $model.value = status.Model
            modelName.value = status.ModelName
            modelFullName.value = status.ModelFullName

            succeed.value = status.Success
            authorized.value = status.Auth
            error.value = status.Error
            completed.value = true

            redirectUrl.value = status.RedirectUrl ?? '/#/signin'
        }
        catch (ex) {
            logException(ex)
        }
    }

    onMounted(() => {
        cqrs.subscribeSetQueryStatus(onSetQueryStatus)
    })

    onUnmounted(() => {
        cqrs.unsubscribeSetQueryStatus(onSetQueryStatus)
    })

    return {
        run,
        query: $query,
        model: $model,
        modelName,
        modelFullName,
        succeed,
        authorized,
        error,
        correlationId,
        completed,
        running,
        redirectUrl
    }
}


export function useCommand(commandFactory: () => any, correlationId = guid()) {
    const $command = ref<any>(null)
    const succeed = ref<boolean>(null)
    const authorized = ref<boolean>(null)
    const validationErrors = ref<any[]>(null)
    const error = ref<any>(null)
    const completed = ref<boolean>(false)
    const invoking = ref<boolean>(false)

    const onSucceedCallbacks: (() => void)[] = []

    function onSucceed(callback: () => void) {
        onSucceedCallbacks.push(callback)
    }

    function invoke(data: any = {}) {
        $command.value = commandFactory()
        $command.value = Object.assign({}, $command.value, data)

        succeed.value = null
        authorized.value = null
        validationErrors.value = []
        error.value = null
        completed.value = false
        invoking.value = true

        cqrs.command($command.value, correlationId)
    }

    function onSetCommandStatus(status: cqrs.ICommandStatus) {
        if (!$command.value) return

        if (status.Command.$type != $command.value.$type || status.CorrelationId != correlationId) return

        //logInfo('Command status', status)

        invoking.value = false
        succeed.value = status.Success
        authorized.value = status.Auth
        validationErrors.value = status.ValidationErrors
        error.value = status.Error

        if (succeed.value) {
            onSucceedCallbacks.forEach(callback => callback())
        }
    }

    onMounted(() => {
        cqrs.subscribeSetCommandStatus(onSetCommandStatus)
    })

    onUnmounted(() => {
        cqrs.unsubscribeSetCommandStatus(onSetCommandStatus)
    })

    return {
        command: $command,
        invoke,
        succeed,
        authorized,
        validationErrors,
        error,
        correlationId,
        completed,
        invoking,
        onSucceed
    }
}